/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-confusing-void-expression */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/unbound-method */
// lots of eslint violoations here whose correction would not serve my purpose.
// no-explicit-any won't work here, we are mutating the type of a class as we
// compose various methods on top of it. No getting around the weirdness
// no-confusing-void-expression and no-unnecessary-condition are unfortunate
// reflections of the fact that a function's return type actually has a typing
// that rises above the normal type system: void vs non-void. I declare that
// this is horseshit, and so allow void to intermingle freely in unions which
// in the context of function return types.
// I would probably go as far as removing this rule globally, except it's quite
// sensible in day to day code, and consumers of these types (i.e. packages'
// hook implementations) won't need to contend with the "distinction",
// lastly: unbound-method I think might be a bug with typescript-eslint not
// being quite updated to use the new typescript 5.3+ compiler. it thinks that
// "return this.[getter]" is returning an unbound method. Of course, I could be
// mistaken and actually implemented the getter wrong somehow, but I think not.

import type { Component as VueComponent, App as VueApp } from 'vue';
import type { GoblinServiceRuntimeSchema } from '@dialpad/thread-goblin';
import type { LogIsh } from './optimistic-logger';
import type { HostContext } from './context';

import { createApp } from 'vue';
import { getLogger, FALLBACK_CONSOLE_LOGISH } from './optimistic-logger';
import {
  HostError,
  IllegalAppHookHostError,
  IllegalGoblinHookHostError,
  IllegalInitHookHostError,
  ProvisionCollisionHostError,
  AlreadyStartedHostError,
  AlreadyHasAppHostError,
  IllegalCleanupProvisionHostError,
} from './error';
import type {
  HostVueCreatedHook,
  HostGoblinHook,
  HostInitHook,
  HostVueMountedHook,
  HostStartedHook,
} from './hook';

import { connect as connectGoblinClient } from '@dialpad/thread-goblin';

// welcome to 2023. if this function doesn't work: you deserve it lol
function isPromise<T>(object: Promise<T> | T): object is Promise<T> {
  return object instanceof Promise;
}
// providing these keys does special stuff (and doesn't extend the host context)
export type ProvideMeta =
  // a function that will be run if the host encounters a fatal error during init
  | 'cleanup'
  // a provided dialpad logger (or suitable facsimile)
  | 'logger';

const PROVIDE_META_KEYS = new Set<string>(['cleanup', 'logger']);

function isMetaKey(key: string): key is ProvideMeta {
  return PROVIDE_META_KEYS.has(key);
}

// Depending on how widely applicable this ends up, we may have to ease up on
// the VueApp constraint and make the builder extensible or figure out a more
// generic approach. Might even already be necessary if we want to use the same
// framework for setting up e2e or even unit test envs.

export type VueAppArgs<RootComponent> = {
  rootComponent: RootComponent;
  // should probably accept a HostElement like real vue does but I don't want to
  // bother figuring out how to type that for node envs at the moment
  mountPoint: string;
};

export class HostBuilder<
  Provided extends object,
  App extends VueApp | never,
  Env extends object | unknown,
> {
  private readonly env: Env;
  private vueApp:
    | ([App] extends [VueApp<infer RC>] ? VueAppArgs<RC> : null)
    | null = null;

  private app: App | null = null;

  // if/when a "real" logger has been created, use that. otherwise, use console.
  private loggerProvided = false;
  private logger: LogIsh;

  private goblin: {
    workerURL: string;
    services: GoblinServiceRuntimeSchema[];
  } | null = null;

  private started = false;

  // global items provided by hooks
  private provided: Record<string, any> = {};

  private readonly hooks = {
    init: [] as HostInitHook<any, any>[],
    goblin: [] as HostGoblinHook<any, any>[],
    create: [] as HostVueCreatedHook<any, any>[],
    mount: [] as HostVueMountedHook<any, any>[],
    start: [] as HostStartedHook<any>[],
    cleanup: [] as ((ex: Error) => void)[],
  };

  constructor(env: Env) {
    this.env = env;
    this.logger = FALLBACK_CONSOLE_LOGISH;
  }

  withGoblin(workerURL: string, services: GoblinServiceRuntimeSchema[]): this {
    // TODO - not sure the best way to do this, but we should be able to verify
    // whether a Host has an Env that satisfies a goblin service's config. Not a
    // priority atm, since services verify input configs at runtime.
    this.assertNotStarted();
    if (typeof (this.env as any).APP_NAME !== 'string')
      throw new HostError(
        'a thread-goblin host env requires a `APP_NAME: string`',
      );
    if (services.length === 0) throw new HostError('no service arguments?');
    if (this.goblin) throw new HostError('withGoblin was already called');
    this.goblin = { workerURL, services };
    return this;
  }

  withVue<RC extends VueComponent>(
    args: VueAppArgs<RC>,
  ): HostBuilder<Provided, VueApp<RC>, Env>;
  withVue<RC extends VueComponent>(
    rootComponent: RC,
    mountPoint?: string,
  ): HostBuilder<Provided, VueApp<RC>, Env>;

  withVue<RC extends VueComponent>(
    rootComponent: RC | VueAppArgs<RC>,
    mountPoint?: string,
  ): this {
    this.assertNotStarted();
    // other app types as well...
    const app = (
      mountPoint !== undefined ? { rootComponent, mountPoint } : rootComponent
    ) as VueAppArgs<RC>;

    if (this.vueApp) throw new AlreadyHasAppHostError(this.vueApp, app);
    (this as any).vueApp = app;
    return this;
  }

  // future capabilities may call for extra lifecycle hooks depending on what
  // sorts of apps we want to support. Thought about providing a programmatic
  // interface for adding lifecycle hooks, but that seems like a lot of meta-
  // effort for very little gain. We can add them manually as needed.

  hostInit<Provides extends object | void>(
    hook: HostInitHook<Provided, never, Env, Provides>,
  ): HostBuilder<Omit<Provides, ProvideMeta> & Provided, App, Env> {
    this.assertNotStarted();
    this.hooks.init.push(hook as any);
    return this as any;
  }

  goblinConnected<Provides extends object | void>(
    hook: HostGoblinHook<Provided, never, Env, Provides>,
  ): HostBuilder<Omit<Provides, ProvideMeta> & Provided, App, Env> {
    this.assertNotStarted();
    this.hooks.goblin.push(hook as any);
    return this as any;
  }

  vueCreated<Provides extends object | void>(
    hook: HostVueCreatedHook<Provided, App, Env, Provides>,
  ): HostBuilder<Omit<Provides, ProvideMeta> & Provided, App, Env> {
    this.assertNotStarted();
    this.hooks.create.push(hook as any);
    return this as any;
  }

  vueMounted<Provides extends object | void>(
    hook: HostVueMountedHook<Provided, App, Env, Provides>,
  ): HostBuilder<Omit<Provides, ProvideMeta> & Provided, App, Env> {
    this.assertNotStarted();
    this.hooks.mount.push(hook as any);
    return this as any;
  }

  hostStarted(
    hook: HostStartedHook<Provided, App, Env>,
  ): HostBuilder<Provided, App, Env> {
    this.assertNotStarted();
    this.hooks.start.push(hook as any);
    return this as any;
  }

  async start(): Promise<HostContext<Provided, App, Env>> {
    this.assertNotStarted();
    this.started = true;

    try {
      this.runHostInitHooks();
      await this.connectGoblinClient();
      await this.runGoblinConnectedHooks();
      this.createApp();
      await this.runVueCreatedHooks();
      this.mountApp();
      await this.runVueMountedHooks();
    } catch (ex) {
      this.logger.error('caught fatal error during bootstrap', ex);
      const error = ex instanceof Error ? ex : new Error(String(ex));
      this.runCleanup(error);
      throw error;
    }

    // happens outside try-catch because we won't let the app die after this
    this.runHostStartedHooks();
    return this.context;
  }

  private runHostInitHooks(): void {
    for (const hook of this.hooks.init) {
      const out = hook(this.context);
      // tbh, rather than straight-up forbidding async inits, maybe we just make
      // it clear that they will not be awaited until more important stuff is
      // ready. idk yet.
      if (isPromise(out)) throw new IllegalInitHookHostError();
      // @ts-expect-error turns out, yes it can
      if (out) this.addProvisions(out as any);
    }
  }

  private async connectGoblinClient(): Promise<unknown> {
    if (!this.goblin) return;
    return connectGoblinClient({
      // still thinking of the best way to constrain Env
      name: (this.env as any).APP_NAME,
      workerURL: this.goblin.workerURL,
      schemas: this.goblin.services,
      config: this.env as any,
    });
  }

  private async runGoblinConnectedHooks(): Promise<void> {
    if (this.hooks.goblin.length === 0) return;
    this.assertHasGoblin();
    for (const hook of this.hooks.goblin) {
      let out = hook(this.context);
      if (isPromise(out)) out = await out;
      // @ts-expect-error turns out, yes it can
      if (out) this.addProvisions(out as any);
    }
  }

  private createApp(): void {
    if (this.vueApp) {
      this.app = createApp(this.vueApp.rootComponent as VueComponent) as any;
    }
  }

  private async runVueCreatedHooks(): Promise<void> {
    if (this.hooks.create.length === 0) return;
    this.assertHasApp();
    for (const hook of this.hooks.create) {
      let out = hook(this.context);
      if (isPromise(out)) out = await out;
      // @ts-expect-error turns out, yes it can
      if (out) this.addProvisions(out as any);
    }
  }

  private mountApp(): void {
    if (!this.app) return;
    if (this.vueApp) {
      this.app.mount(this.vueApp.mountPoint);
    }
  }

  private async runVueMountedHooks(): Promise<void> {
    if (this.hooks.mount.length === 0) return;
    this.assertHasApp();
    for (const hook of this.hooks.mount) {
      let out = hook(this.context);
      if (isPromise(out)) {
        this.logger.warn(
          `async mounted hook: consider making this a create hook
                         if it matters so much?`.replace(/\s+/g, ' '),
        );
        out = await out;
      }
      // @ts-expect-error turns out, yes it can
      if (out) this.addProvisions(out as any);
    }
  }

  private runHostStartedHooks(): void {
    if (this.hooks.start.length === 0) return;
    for (const hook of this.hooks.start) {
      try {
        const out = hook(this.context);
        if (isPromise(out)) {
          out
            .then((returned) => {
              if (returned !== undefined)
                this.logger.warn(
                  'started hook resolved to non-undef value, which we will ignore',
                  { hook, returned },
                );
            })
            .catch((error) => {
              this.logger.error('async started hook rejected (non-fatal)', {
                hook,
                error,
              });
            });
          // @ts-expect-error turns out, yes it can
        } else if (out) {
          this.logger.warn(
            'started hook returned non-undef value, which we will ignore',
            { hook, returned: out },
          );
        }
      } catch (error) {
        this.logger.error('started hook threw exception (non-fatal)', {
          hook,
          exception: error,
        });
      }
    }
  }

  private runCleanup(ex: Error): void {
    for (const cleanup of this.hooks.cleanup) {
      try {
        cleanup(ex);
      } catch (ex) {
        this.logger.warn('another exception during cleanup:', ex);
      }
    }
    this.hooks.cleanup.splice(0);
  }

  private addProvisions(provisions?: Record<string, any>): void {
    if (!provisions) return;

    const keys = Object.keys(provisions);
    for (const key of keys.filter(isMetaKey))
      this.addMetaProvision(key, provisions[key]);

    for (const key in provisions) {
      if (isMetaKey(key)) continue; // we JUST did those
      const value = (provisions as any)[key];
      if (key in this.provided) {
        const existing = this.provided[key];
        this.logger.error('provision collision context:', {
          key,
          value,
          existing,
        });
        throw new ProvisionCollisionHostError(key, existing, value);
      } else {
        this.provided[key] = value;
      }
    }
  }

  private addMetaProvision(key: ProvideMeta, value: any): void {
    switch (key) {
      case 'cleanup':
        if (typeof value === 'function') {
          this.hooks.cleanup.push(value);
          return;
        } else {
          throw new IllegalCleanupProvisionHostError(value);
        }
      case 'logger':
        this.addLogger(value);
        return;
    }
  }

  private addLogger(logger: LogIsh): void {
    if (this.loggerProvided) {
      this.logger.warn('got an extra logger, ignoring it', logger);
      return;
    }

    try {
      const gotLogger = getLogger(logger);
      if (gotLogger) this.logger = gotLogger;
      else
        this.logger.warn(`provided logger apparently isn't! ignoring`, logger);
    } catch (ex) {
      this.logger.warn('(full exception for failed getLogger)', ex);
      const text = ex instanceof Error ? ex.message : String(ex);
      throw new HostError(`fatal error while getting logger: ${text}`);
    }
    // either we use the one we got, or keep the fallback. either way, no one
    // can add more.
    this.loggerProvided = true;
  }

  private get context(): HostContext<Provided, App, Env> {
    return {
      ...(this.provided as Provided),
      vueApp: this.vueApp ? this.app : null, // TODO - other app types etc
      env: this.env,
      logger: this.logger,
    } as HostContext<Provided, App, Env>;
  }

  private assertNotStarted(): void {
    if (this.started) throw new AlreadyStartedHostError();
  }

  private assertHasApp(): void {
    // TODO - other app types?
    if (this.vueApp === null) throw new IllegalAppHookHostError();
  }

  private assertHasGoblin(): void {
    if (this.goblin === null) throw new IllegalGoblinHookHostError();
  }
}
